'use client';

import { bucketByGroup } from '@zealot/ui/lib/bucketByGroup';
import type { FilterConfig, FilterOption, MultiSelectFilterConfig } from '@zealot/ui/lib/filtering/filterConfig';
import { cn } from '@zealot/ui/lib/utils';
import { format } from 'date-fns';
import { ChevronRight } from 'lucide-react';
import * as React from 'react';
import type { DateRange } from 'react-day-picker';
import { AsyncCombobox } from '../async-combobox';
import { ComboboxMultiSelect } from '../combobox-multi-select';
import { DateRangePicker } from '../date-picker';
import { Input } from '../input';
import { ToggleGroup, ToggleGroupItem } from '../toggle-group';

type DateRangeValue = { from: string | null; to: string | null };

// gloss
const hasActiveValue = (config: FilterConfig, value: unknown): boolean => {
  switch (config.kind) {
    case 'multiSelect':
      return Array.isArray(value) && value.length > 0;
    case 'dateRange': {
      const range = value as DateRangeValue | undefined;
      return Boolean(range?.from || range?.to);
    }
    case 'text':
      return Boolean(value);
    case 'boolean':
      return value !== null && value !== undefined;
  }
};

// gloss
const emptyValueFor = (config: FilterConfig): unknown => {
  switch (config.kind) {
    case 'multiSelect':
      return [];
    case 'dateRange':
      return { from: null, to: null };
    case 'text':
      return '';
    case 'boolean':
      return null;
  }
};

// gloss
const parseLocalDate = (isoDate: string): Date => {
  const [year = 0, month = 1, day = 1] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// gloss
const toDateRange = (value: DateRangeValue | undefined): DateRange | undefined => {
  if (!value?.from && !value?.to) return undefined;
  return {
    from: value?.from ? parseLocalDate(value.from) : undefined,
    to: value?.to ? parseLocalDate(value.to) : undefined,
  };
};

const fromDateRange = (range: DateRange | null): DateRangeValue => ({
  from: range?.from ? format(range.from, 'yyyy-MM-dd') : null,
  to: range?.to ? format(range.to, 'yyyy-MM-dd') : null,
});

// gloss
const chipsBoxClassName = 'max-h-20 shrink-0 overflow-y-auto';

// gloss
const AsyncMultiSelectLeaf = ({
  config,
  selected,
  onSearch,
  onChange,
}: {
  config: MultiSelectFilterConfig;
  selected: string[];
  onSearch: (query: string) => void;
  onChange: (values: string[]) => void;
}) => {
  const seenOptions = React.useRef(new Map<string, FilterOption>());
  for (const option of config.options) seenOptions.current.set(option.value, option);
  const value = selected.map((v) => seenOptions.current.get(v) ?? { value: v, label: v });

  return (
    <AsyncCombobox<FilterOption>
      multiple
      items={config.options}
      value={value}
      inline
      className={chipsBoxClassName}
      onValueChange={(items) => onChange(items.map((item) => item.value))}
      onSearch={onSearch}
      isLoading={config.isLoadingOptions}
      itemToString={(option) => option.label}
      itemToValue={(option) => option.value}
      placeholder={`Search ${config.label}...`}
    />
  );
};

// gloss
const FilterMenuLeaf = ({
  config,
  value,
  onChange,
}: {
  config: FilterConfig;
  value: unknown;
  onChange: (value: unknown) => void;
}) => {
  switch (config.kind) {
    case 'multiSelect': {
      const selected = (value as string[] | undefined) ?? [];
      if (config.onSearch) {
        return (
          <AsyncMultiSelectLeaf
            config={config}
            selected={selected}
            onSearch={config.onSearch}
            onChange={(values) => onChange(values)}
          />
        );
      }
      return (
        <ComboboxMultiSelect
          items={config.options}
          value={selected}
          inline
          isLoading={config.isLoadingOptions}
          className={chipsBoxClassName}
          onValueChange={onChange}
          placeholder={`Search ${config.label}...`}
        />
      );
    }
    case 'dateRange':
      return (
        <DateRangePicker
          value={toDateRange(value as DateRangeValue | undefined) ?? null}
          onValueChange={(range) => onChange(fromDateRange(range))}
        />
      );
    case 'text':
      return (
        <Input
          value={(value as string | undefined) ?? ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder={config.label}
        />
      );
    case 'boolean': {
      const boolValue = value as boolean | null | undefined;
      const pressed = boolValue == null ? [] : [String(boolValue)];
      return (
        <ToggleGroup
          variant="outline"
          className="w-full gap-0"
          value={pressed}
          onValueChange={(next) => onChange(next.length === 0 ? null : next[0] === 'true')}
        >
          <ToggleGroupItem value="true" className="flex-1 cursor-pointer rounded-r-none">
            {config.trueLabel ?? 'Yes'}
          </ToggleGroupItem>
          <ToggleGroupItem value="false" className="-ml-px flex-1 cursor-pointer rounded-l-none">
            {config.falseLabel ?? 'No'}
          </ToggleGroupItem>
        </ToggleGroup>
      );
    }
  }
};

// gloss
const FilterMenuCategory = ({
  config,
  isActive,
  isSet,
  onActivate,
}: {
  config: FilterConfig;
  isActive: boolean;
  isSet: boolean;
  onActivate: () => void;
}) => (
  <button
    type="button"
    title={config.label}
    className={cn(
      'flex w-full cursor-pointer items-center justify-between px-3 py-2 text-sm transition-colors',
      isActive ? 'bg-primary/5 font-medium text-primary' : 'text-foreground hover:bg-muted',
    )}
    onMouseEnter={onActivate}
    onFocus={onActivate}
    onClick={onActivate}
  >
    <div className="flex min-w-0 items-center gap-2">
      {config.icon}
      <span className="truncate">{config.label}</span>
    </div>
    <div className="flex shrink-0 items-center gap-1">
      {isSet && <span className="size-1.5 rounded-full bg-primary" />}
      <ChevronRight className="size-3.5 text-muted-foreground" />
    </div>
  </button>
);

type FilterMenuProps = {
  configs: FilterConfig[];
  values: Record<string, unknown>;
  onValueChange: (key: string, value: unknown) => void;
  className?: string;
};

// gloss
const FilterMenu = ({ configs, values, onValueChange, className }: FilterMenuProps) => {
  const [activeKey, setActiveKey] = React.useState<string | null>(null);
  const activeConfig = configs.find((config) => config.key === activeKey) ?? null;

  return (
    <div data-slot="filter-menu" className={cn('flex min-h-[20rem]', className)}>
      <div className="w-56 border-border border-r">
        <div className="border-border border-b px-3 py-2 font-semibold text-foreground text-xs">Add Filter...</div>
        {}
        <div className="max-h-[60vh] overflow-y-auto py-1">
          {[...bucketByGroup(configs)].map(([group, groupConfigs]) => (
            <div key={group}>
              {}
              {group && (
                <div className="px-3 pt-2 pb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  {group}
                </div>
              )}
              {groupConfigs.map((config) => (
                <FilterMenuCategory
                  key={config.key}
                  config={config}
                  isActive={config.key === activeKey}
                  isSet={hasActiveValue(config, values[config.key])}
                  onActivate={() => setActiveKey(config.key)}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      {}
      <div className="relative w-64">
        {activeConfig && (
          <div className="absolute inset-0 flex flex-col p-3">
            <FilterMenuLeaf
              key={activeConfig.key}
              config={activeConfig}
              value={values[activeConfig.key]}
              onChange={(value) => onValueChange(activeConfig.key, value)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export type { FilterMenuProps };
export { emptyValueFor, FilterMenu, hasActiveValue };
